import { ShareFlyerDrawer } from '@/components/ShareFlyerDrawer';
import config from '@/config/config';
import { AidaStatus, APP_ROUTES, DEFAULT_ERROR_MESSAGE, FlyerType } from '@/config/constants';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { BodySmall, useSnackbar } from '@/design-system';
import { SAVE_FLYER_MUTATION } from '@/graphql/mutation';
import { flyerVar } from '@/graphql/state';
import { CallToActionEnum, Flyer, GetDistrictResponse, SaveFlyerResponse } from '@/graphql/types';
import { TrackingFrom, useMetrics, useNavigateWithTracking } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import colors from '@/theme/colors';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import getFeatureLinkTracking from '@/utils/featureLinksTracking';
import { getCTAMetric } from '@/utils/metrictCTAType';

import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { cx } from '@emotion/css';
import { Skeleton } from '@mui/material';
import { useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearSavedFlyersCache } from '@/pages';
import {
  FlyerDetailsBottom,
  FlyerDetailsHeader,
  FlyerDetailsInfo,
  FlyerDetailsSwiper,
} from '../index';
import { FlyerDetailsBottomRef } from '../FlyerDetailsBottom/FlyerDetailsBottom';
import { FlyerAvailability } from '../../components/FlyerAvailability/FlyerAvailability';
import { PrintFlyer } from '../../components/PrintFlyer';
import { SimilarFlyers } from '../../components/SimilarFlyers';
import styles from './ActiveFlyerDetails.styles';
import { GET_DISTRICT } from '@/graphql/queries';

interface Props {
  backButton: () => void;
  flyer: Flyer;
  trackingFrom: TrackingFrom;
  loadingFlyer: boolean;
  onLoadFlyer: () => void;
  trackId: string;
  showLoginDrawer: () => boolean;
  isCommunity: boolean;
  flyerTitle: string;
  isCommunityFree: boolean;
  isEmbedded: boolean;
}

const Skeletons = ({ isEmbedded = false }: { isEmbedded?: boolean }) => {
  const skeletonContent = (
    <div
      className={cx(styles.flyerInfoContainer, styles.skeleton)}
      data-testid={isEmbedded ? 'embeddedSkeletonContainer' : 'skeletonContainer'}
    >
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
    </div>
  );

  if (isEmbedded) {
    return <div className={cx(styles.wrapper, styles.embedBorder)}>{skeletonContent}</div>;
  }

  return skeletonContent;
};

export const ActiveFlyerDetails: React.FC<Props> = ({
  backButton,
  loadingFlyer,
  onLoadFlyer,
  trackId,
  trackingFrom,
  flyer,
  showLoginDrawer,
  isCommunity,
  flyerTitle,
  isCommunityFree,
  isEmbedded,
}) => {
  const client = useApolloClient();
  const params = useParams();
  const navigate = useNavigate();
  const navigateWithTracking = useNavigateWithTracking();
  const { trackEvent } = useAnalytics();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const audienceId = searchParams.get('audienceId');
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState<boolean>(false);
  const flyerDetailsBottomRef = useRef<FlyerDetailsBottomRef>(null);
  const extraParams = `?isParentExperience=true${audienceId ? `&audienceId=${audienceId}` : ''}`;
  const shareUrl = `${config.REACT_APP_SHARE_HOST}/flyers/${params?.id}/districts/${params?.districtId}${extraParams}`;
  const flyerUrlLink = `${window.location.origin}${window.location.pathname}`;

  const showToast = useSnackbar();

  const { trackMetrics } = useMetrics();

  const flyerType = flyer?.type ?? FlyerType.school;

  const { track } = useAnalytics();
  const [saveFlyer] = useMutation<SaveFlyerResponse>(SAVE_FLYER_MUTATION);
  const { data: getDistrictData } = useQuery<GetDistrictResponse>(GET_DISTRICT, {
    fetchPolicy: 'cache-first',
    variables: { districtId: Number(params?.districtId) },
    skip: !params?.districtId,
  });

  const district = getDistrictData?.getDistrict;

  const goToOrgProfile = () => {
    if (flyer?.org) {
      track({
        action: GenericTrackTypes.BUTTON_PRESSED,
        category: `${trackId}:go_to_org_profile`,
        label: flyer?.org.name,
        value: Number(flyer?.org.orgId),
      });
      navigate(`/org/${flyer.org?.orgId}/profile`);
    }
  };

  const onClickFavorite = async () => {
    if (flyer && !showLoginDrawer()) {
      try {
        client.cache.modify({
          id: `Flyer:{"flyerId":${flyer.flyerId}}`,
          fields: {
            isFavorite: () => !flyer.isFavorite,
          },
        });
        const { data } = await saveFlyer({
          variables: {
            input: {
              flyerId: flyer.flyerId,
              isFavorite: !flyer.isFavorite,
            },
          },
        });
        if (data?.saveFlyer.flyerId) {
          flyerVar({
            ...flyer,
            isFavorite: !flyer.isFavorite,
          });
          const action = flyer.isFavorite ? 'pa-unsave' : 'pa-save';
          trackMetrics({
            flyerId: flyer.flyerId.toString(),
            metric: 'thumbnail-save',
            action,
            resource: 'districts',
            resourceId: flyer?.districtId.toString() ?? '',
            audienceId,
            distributionsReleased: flyer.distributionsReleased,
          });

          trackEvent({
            event: flyer.isFavorite ? TrackingEvents.unsaveFlyer : TrackingEvents.saveFlyer,
            event_detail: flyer.isFavorite
              ? EventDetails.unsaveFlyer(flyerType)
              : EventDetails.saveFlyer(flyerType),
            stage: TrackingStage.consideration,
            ...trackingFrom,
          });

          if (!flyer.isFavorite) {
            showToast({
              message: 'The flyer has been saved.',
              buttonLabel: 'View Saved',
              onClick: () => navigateWithTracking(APP_ROUTES.saved, trackingFrom),
              duration: 5000,
            });
          } else {
            showToast({
              message: 'Flyer unsaved.',
              duration: 5000,
            });
          }
          clearSavedFlyersCache(client.cache);
        }
      } catch (err) {
        showToast({ message: DEFAULT_ERROR_MESSAGE });
      }
    }
  };

  const onShare = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:share_flyer`,
      label: flyer?.title,
      value: Number(flyer?.flyerId),
    });
    setIsShareDrawerOpen(true);
  };

  const downloadPDF = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'flyer.pdf';
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click'));
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:view_flyer`,
      label: flyer?.title,
      value: Number(flyer?.flyerId),
    });
  };

  const openLink = ({
    origin,
    value,
    cta,
    featureId,
  }: {
    origin: string;
    value: string;
    cta: string;
    featureId?: string | null;
  }) => {
    const trackingData = getFeatureLinkTracking(cta as CallToActionEnum);
    trackEvent({
      ...trackingData,
      ...trackingFrom,
    });
    trackCTAMetrics(cta, featureId);
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:${origin}:link`,
      label: value,
    });
    switch (cta) {
      case CallToActionEnum.Call:
        {
          window.location.href = 'tel:' + value;
        }
        break;
      case CallToActionEnum.Email:
        {
          window.open(`mailto:${value}`);
        }
        break;
      default:
        window.open(value);
    }
  };

  const trackCTAMetrics = (type: string, featureId?: string | null) => {
    const metric = getCTAMetric(type) ?? '';
    trackMetrics({
      flyerId: flyer!.flyerId.toString(),
      metric,
      action: 'pa-cta',
      resource: 'districts',
      resourceId: flyer?.districtId.toString() ?? '',
      featureId: featureId ?? metric,
      audienceId,
      distributionsReleased: flyer.distributionsReleased,
    });
  };

  const trackShareOptions = (action: string) => {
    trackMetrics({
      flyerId: flyer!.flyerId.toString(),
      metric: '',
      action,
      resource: 'districts',
      resourceId: flyer?.districtId.toString() ?? '',
      audienceId,
      distributionsReleased: flyer.distributionsReleased,
    });
  };

  const navigateToViewLocation = () => {
    if (flyer?.mapUrl) {
      navigate(`${APP_ROUTES.maps}?url=${flyer.mapUrl}`);
      const mapsTracking = getFeatureLinkTracking(CallToActionEnum.Map);
      trackEvent({
        ...mapsTracking,
        ...trackingFrom,
      });

      trackMetrics({
        flyerId: flyer!.flyerId.toString(),
        metric: 'pa-view-location',
        resource: 'districts',
        action: 'pa-view-location',
        resourceId: flyer?.districtId.toString() ?? '',
        audienceId,
        distributionsReleased: flyer.distributionsReleased,
      });
    }
  };

  const trackCTAButton = (
    buttonName: string,
    link: string,
    type: string,
    featureId?: string | null
  ) => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:${buttonName}:cta_button`,
      label: link,
    });
    trackCTAMetrics(type, featureId);
    const trackingData = getFeatureLinkTracking(type as CallToActionEnum);
    trackEvent({
      ...trackingData,
      ...trackingFrom,
    });
  };

  const addToCalendar = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:add_to_calendar`,
      label: flyer?.title,
      value: flyer?.flyerId,
    });
  };

  const contactClick = (origin: string, email: string) => {
    window.open(`mailto:${email}`, '_self');
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:${origin}:contact`,
      label: flyer?.title,
      value: flyer?.flyerId,
    });
  };

  const slideSwiper = () => {
    track({
      category: `${trackId}:swiper`,
      action: GenericTrackTypes.SLIDE,
      label: flyer?.title,
      value: flyer?.flyerId,
    });
  };

  return (
    <>
      <FlyerDetailsHeader
        onShare={onShare}
        isFavorite={flyer.isFavorite}
        goToPreviousPage={backButton}
        onClickFavorite={onClickFavorite}
        flyerType={flyerType}
        isEmbedded={isEmbedded}
        flyerTitle={flyerTitle}
      />
      <main>
        <div className={cx(styles.wrapper, isEmbedded && styles.embedFlyerBorder)}>
          <FlyerDetailsSwiper
            flyer={flyer}
            loadingFlyer={loadingFlyer}
            onLoadFlyer={onLoadFlyer}
            slideSwiper={slideSwiper}
            audienceId={audienceId}
            trackingFrom={trackingFrom}
            flyerTitle={flyerTitle}
            aidaStatus={district?.communitySettings?.aidaStatus as AidaStatus}
          />
        </div>
        {loadingFlyer ? (
          <Skeletons isEmbedded={isEmbedded} />
        ) : (
          <>
            <div className={cx(styles.wrapper, isEmbedded && styles.embedBorder)}>
              <div className={styles.flyerInfoContainer}>
                <FlyerDetailsInfo
                  flyer={flyer}
                  isCommunity={isCommunity}
                  goToOrgProfile={goToOrgProfile}
                  onShare={onShare}
                  downloadPDF={downloadPDF}
                  addToCalendar={addToCalendar}
                  openLink={openLink}
                  emailClick={contactClick}
                  viewLocation={navigateToViewLocation}
                  flyerTitle={flyerTitle}
                  district={district}
                  isEmbedded={isEmbedded}
                  focusCTA={
                    flyer.ctas.length > 0
                      ? () => {
                          flyerDetailsBottomRef.current?.focusCTA();
                        }
                      : undefined
                  }
                />
                {!isEmbedded && (
                  <>
                    {flyer.schools && <FlyerAvailability schools={flyer.schools} disabled={false} />}
                    <SimilarFlyers
                      categories={flyer.categories}
                      flyerType={flyerType}
                      disabled={false}
                      isCommunityFree={isCommunityFree}
                    />
                  </>
                )}
              </div>
              {/* {isCommFree && flyer.org && (
              <div className={styles.providedBy}>
                <div className={styles.flex}>
                  <ProvidedByCommunity
                    org={flyer.org}
                    goToOrgProfile={goToOrgProfile}
                    disabled={isNonActive}
                  />
                  {!isEmbedded && (
                    <>
                      {flyer.schools && (
                        <FlyerAvailability schools={flyer.schools} disabled={false} />
                      )}
                      <SimilarFlyers
                        categories={flyer.categories}
                        flyerType={flyerType}
                        disabled={false}
                        isCommunityFree={isCommunityFree}
                      />
                    </>
                  )}
                </div>
                {isCommunity && flyer.org && (
                  <div className={styles.providedBy}>
                    <div className={styles.flex}>
                      <ProvidedByCommunity
                        org={flyer.org}
                        goToOrgProfile={goToOrgProfile}
                        />
                    </div>
                  </div>
                )}
                {!isEmbedded && (
                  <div className={styles.flyerIdContainer}>
                    <BodySmall color={colors.slate}>Flyer ID {flyer.flyerId}</BodySmall>
                  </div>
                )}
              </div>
            )} */}
              {!isEmbedded && (
                <div className={styles.flyerIdContainer}>
                  <BodySmall color={colors.slate}>Flyer ID {flyer.flyerId}</BodySmall>
                </div>
              )}
            </div>
            <FlyerDetailsBottom
              ref={flyerDetailsBottomRef}
              ctas={flyer.ctas}
              trackCTAButton={trackCTAButton}
            />
          </>
        )}
      </main>
      <ShareFlyerDrawer
        open={isShareDrawerOpen}
        onClose={() => setIsShareDrawerOpen(false)}
        thumbnail={flyer.frontImageUrl}
        userText={flyer.pages?.[0]?.userText ?? ''}
        link={flyerUrlLink}
        pdfUrl={flyer.pdfUrl}
        flyerTitle={flyer.title}
        shareUrl={shareUrl}
        trackMetrics={trackShareOptions}
        trackingFrom={trackingFrom}
        isQuarantined={flyer.isQuarantined}
        districtId={params?.districtId}
      />
      <PrintFlyer pages={flyer.pages} />
    </>
  );
};
